import { z } from 'zod';

import { zodEditStateEnum, editStateEnum } from './data-model.enums';
import { Tokens } from './data-model.tokens';
import { Token, tokenDTO, tokenSchema } from './data-model.token';
import { CapicheLoggerCore } from '@capiche/ts-logger-core';

const logger: CapicheLoggerCore = new CapicheLoggerCore('sentence:', false);

// definition of token alignment between input tokens and GPT sentence.suggestion
const zodTokenAlignment = z.strictObject({
  gptIndex: z.number().nullable(), // the char index in the sentence.suggestion string
  gptLength: z.number().nullable(), // the length of the token in the GPT suggestion
  gptString: z.string().nullable(), // the token string in the GPT suggestion

  tokenIndex: z.number().nullable(), // the index of the token in sentence.tokens

  alignment: z.enum(['match', 'suggestion', 'similar', 'sequence', 'discard']), // type of alignment
  similarity: z.number(), // 0 to 1 similarity score between the token strings
});

export type tokenAlignment = z.infer<typeof zodTokenAlignment>;

// the sentence object schema
//  each sentence is seperated by any non alphabet and white space character from the input string
//  each sentence has an index and a length which represent the position of the sentence and the corrected sentence by LLM
export const sentenceSchema = z.strictObject({
  // optional source ID for tracking
  srcId: z.string().optional(),
  // sequence ID for versioning
  seqId: z.number().int().min(0).optional(),
  // TODO: check if this comment is correct: the sentence text as provided by the user, including original case and any/all punctuation etc.
  input: z.string(),
  // the index of the token in the input string
  index: z.number().int().min(0),
  // the length of the token in the input string
  length: z.number().int().min(0),
  // the state of the sentence in the editor
  // this state is used by the client to control the display of suggestions
  //  for example sentence that have been replaced by the user should not show suggestions
  editState: zodEditStateEnum,

  // the spelling state of the sentence in the editor
  // the state refects the most severe state of any of the tokens in the sentence
  // misspelled > contextual > correct
  // spellingState: zodSpellingStateEnum, // TODO: this should be added

  // the tokens that client provides to the backend from input string
  // the backend processes and responds only the provided tokens only
  tokens: z.array(tokenSchema),
  //the corrected sentence by LLM
  suggestion: z.string(),

  alignment: z.array(zodTokenAlignment).optional(),
});

export type sentenceDTO = z.infer<typeof sentenceSchema>;

export class Sentence {
  constructor(public readonly dto: sentenceDTO) {
    logger.verbose('constructor:', dto);
  }

  // Factory methods
  static fromDTO(dto: sentenceDTO): Sentence {
    sentenceSchema.parse(dto);
    return new Sentence(dto);
  }

  static fromString(text: string, index: number = 0, seqId?: number, srcId?: string): Sentence {
    logger.verbose('fromString:', text, seqId, srcId);
    const tokens: Tokens = Tokens.fromString(text);

    return new Sentence({
      srcId: srcId,
      seqId: seqId,
      input: text,
      suggestion: '', // TODO: set by the LM - should this be initialized to undefined?
      index: index,
      length: text.length,
      editState: editStateEnum.new,
      tokens: tokens.toDTOs(),
    });
  }

  // Serialization
  toDTO(): sentenceDTO {
    return this.dto;
  }

  // alignment methods
  gptAlignTokens(): tokenAlignment[] {
    const alignmentRank = { match: 4, suggestion: 3, similar: 2, sequence: 1, discard: 0 }; // define ranking preference
    const results: tokenAlignment[] = []; // array of tokenAlignment results

    const inputTokens: Tokens = this.getTokens();
    const gptTokens: Tokens = Tokens.fromString(this.dto.suggestion);
    const inputCount: number = inputTokens.getTokenCount();
    const gptCount: number = gptTokens.getTokenCount();

    logger.log(`gptAlignTokens: input: ${inputCount} gpt: ${gptCount}`);

    // track indices for both input and gpt sequences
    let gptIndex: number = 0;
    for (let inputIndex: number = 0; inputIndex < inputCount; inputIndex++) {
      const inputToken: Token | undefined = inputTokens.get(inputIndex); // get the input token
      if (!inputToken) {
        logger.warn(`gptAlignTokens: missing token at index ${inputIndex} - skipping`);
        continue;
      }

      const gptRemaining: number = gptCount - gptIndex;
      const inputRemaining: number = inputCount - inputIndex;

      // recompute delta at each iteration based on remaining lengths
      // define direction of comparison and windows size
      // -ve delta means input is longer than gpt - some input tokens will not have gpt counterparts
      // +ve delta means gpt is longer than input - some gpt tokens will not have input counterparts
      const delta: number = gptRemaining - inputRemaining;

      // get all the potential matching gpt tokens based on delta direction and window size
      // Minimum lookahead of 1 extra token (window size ≥ 2) so the algorithm can see past a
      // compound-word split when net delta=0 (e.g. "thismorning"→"this morning" cancelled by a
      // GPT word drop).  With Math.max(delta,0) the window collapses to 1 token when delta=0,
      // forcing pure ordinal alignment and cascading mismatches for all subsequent tokens.
      // Math.max(delta,1) handles the single-split/single-drop case; multiple concurrent
      // splits/drops with net delta≈0 require the DP alignment planned in CSW-1355.
      const gptRange: Token[] = gptTokens.getRange(gptIndex, Math.max(delta, 1)); // get all the potential matching gpt tokens

      // initialize to store the best matched gptToken for this inputToken
      let bestMatch: { token: Token; similarity: number; alignment: keyof typeof alignmentRank } | null = null;

      // for each of the possible gptTokens in gptRange, compare against the inputToken
      for (const gptToken of gptRange) {
        const match: boolean = inputToken.normalMatches(gptToken); // CSW-1573: compare normalised forms (e.g. ' vs ’)
        const suggestion: boolean = inputToken.hasSuggestion(gptToken.dto.input);
        const similarity = inputToken.similarityTo(gptToken);
        const similar: boolean = similarity > 0.7;
        const sequence: boolean = delta === 0; // when delta is 0, we are in sequence alignment mode

        // determine alignment for this gptToken
        const alignment: keyof typeof alignmentRank = match
          ? 'match'
          : suggestion
            ? 'suggestion'
            : similar
              ? 'similar'
              : sequence
                ? 'sequence'
                : 'discard';

        // store the best matched gptToken of those in gptRange
        if (
          !bestMatch ||
          alignmentRank[alignment] > alignmentRank[bestMatch.alignment] ||
          (alignmentRank[alignment] === alignmentRank[bestMatch.alignment] && similarity > bestMatch.similarity)
        ) {
          bestMatch = { token: gptToken, similarity, alignment };
        }
      }

      // guard against null bestMatch
      if (!bestMatch) continue;

      // build the result
      const gptDTO: tokenDTO = bestMatch.token.toDTO();
      const result: tokenAlignment = {
        tokenIndex: inputIndex,
        gptIndex: gptDTO.index,
        gptLength: gptDTO.length,
        gptString: gptDTO.input,
        alignment: bestMatch.alignment,
        similarity: bestMatch.similarity,
      };

      // log the result
      logger.log(
        `gptAlignTokens: compare: ` +
          `${result.tokenIndex ?? 'null'} ` +
          `${result.alignment.padEnd(10, ' ')} ${result.similarity.toFixed(2)} ` +
          `${result.tokenIndex != null ? this.dto.tokens[result.tokenIndex].input : 'n/a'} ` +
          `> ${result.gptString}` +
          ` (${result.gptIndex},${result.gptLength}) ` +
          `range: [${gptRange.map((t) => t.dto.input).join(', ')}]` +
          ` delta=${delta}`,
      );

      // store the result
      results.push(result);

      // determine how many GPT tokens were effectively consumed
      //   const indx = gptRange.indexOf(bestMatch.token);
      //    logger.log(`gptAlignTokens: bestMatch token index in gptRange: ${indx}`);
      const step = Math.max(1, gptRange.indexOf(bestMatch.token) + 1);

      // advance GPT index only when we actually consumed tokens
      if (bestMatch.alignment !== 'discard') {
        gptIndex += step;
      }
    }

    return results;
  }

  // testing helper to output the alignments in a format that easy for writing test comparisons
  getAlignmentStrings(): string[] {
    const r: string[] = [];
    const a: tokenAlignment[] = this.gptAlignTokens();

    a.forEach((align) => {
      r.push(
        align.alignment === 'discard'
          ? `${align.tokenIndex ?? 'null'} ${align.tokenIndex !== null ? this.dto.tokens[align.tokenIndex].input : 'n/a'} > discard`
          : `${align.tokenIndex ?? 'null'} ${align.tokenIndex !== null ? this.dto.tokens[align.tokenIndex].input : 'n/a'} > ${align.gptString} (${align.gptIndex},${align.gptLength})`,
      );
      // logger.log('getAlignmentStrings: return:', r[r.length - 1]);
    });
    return r;
  }

  // Business logic
  // called when the user make a change to the input field (type/paste/delete etc..)
  handleUserInput(input: string): Sentence {
    // TODO: should this be passsed in from the parent so that a particualr seqId represnts the last usr input that an element was based on?
    logger.verbose('handleUserInput:', this.dto, input);

    const inputSeqId: number = (this.dto.seqId ?? 0) + 1;
    const inputTokens: Tokens = Tokens.fromString(input);

    // somehow compare inputTokens with this.dto.tokens
    // if the tokens are the same, use this.dto.tokens
    // if the token has changed, use the inputTokens and mark token as needing reprocessing
    // build outputTokens from this logic
    const outputTokens: Tokens = inputTokens;

    return new Sentence({
      ...this.dto,
      seqId: inputSeqId,
      input: input,
      suggestion: '', // reset on user input
      length: input.length,
      editState: editStateEnum.pending, // mark as needing reprocessing
      tokens: outputTokens.toDTOs(),
    });
  }

  // called when capiche responds with suggestions
  handleCapicheResponse(response: sentenceDTO): Sentence {
    const existingSentence: Sentence = this;
    const responseSentence: Sentence = Sentence.fromDTO(response);

    if (responseSentence.dto.srcId !== existingSentence.dto.srcId) return existingSentence;
    if ((responseSentence.dto.seqId ?? 0) < (existingSentence.dto.seqId ?? 0)) return existingSentence;

    // TODO: this is not the correct assumption
    // capiche will typically be called with a sub-set of the entire document and tries to only submit
    //   text that requires processing for lookup - this means that many responses will not contain the full sentence
    //   and will only contain the tokens that required processing.
    //   this logic exists at all levels of the data model, so it is not specific to sentences.
    //
    // You assume that the server returns the full enriched sentence.
    // So you fully replace tokens and suggestion here.
    return new Sentence({
      ...existingSentence.dto,
      suggestion: responseSentence.dto.suggestion ?? existingSentence.dto.suggestion,
      tokens: responseSentence.dto.tokens,
      editState: editStateEnum.checked,
    });
  }

  isValidIn(parentInput: string): boolean {
    const idx = parentInput.indexOf(this.dto.input, this.dto.index);
    return this.dto.index === idx && this.dto.length === this.dto.input.length;
  }

  // refresh Tokens .. retain any tokens that are replaced
  // .. generate fresh tokens from the sentence string
  // .. and return a new Sentence instance with the combined tokens
  //  withFreshTokens(): Sentence {
  //    // Preserve tokens with state === 'replaced'
  //    // const tokens: Tokens = Tokens.fromDTOs(this.dto.tokens);
  //    const preservedTokens: tokenDTO[] = this.dto.tokens.filter((t: tokenDTO) => t.state === editStateEnum.replaced);
  //
  //    const sentence: Sentence = Sentence.fromString(
  //      this.dto.sentenceString,
  //      this.dto.index,
  //      this.dto.seqId,
  //      this.dto.srcId,
  //    );
  //    const freshTokens: Tokens = sentence
  //      .getTokens()
  //      .filter((t: Token) => preservedTokens.some((p) => p.index === t.dto.index && p.length === t.dto.length)); // preserved.some should be inValidIn()??
  //
  //    //    return ([...preservedTokens, ...rfreshTokens]);
  //    //    // Generate fresh tokens from the sentence string
  //    //    const freshTokens :Tokens  = Sentence.fromString(
  //    //      this.dto.sentenceString,
  //    //      this.dto.index,
  //    //      this.dto.seqId,
  //    //      this.dto.srcId,
  //    //    ).dto.tokens.filter((t) => !preserveTokens.some((p) => p.index === t.index && p.length === t.length));
  //    //
  //
  //    const y: sentenceDTO = { ...this.toDTO(), tokens: [...preservedTokens.toDTOs(), ...rfreshTokens.toDTOs()] };
  //
  //    // Return a new Sentence instance with combined tokens
  //    return y.fromDTO();
  //  }

  // Business logic
  getTokens(): Tokens {
    return Tokens.fromDTOs(this.dto.tokens);
  }

  getTokenCount(): number {
    return this.dto.tokens.length;
  }

  hasMisspelledTokens(): boolean {
    return this.getTokens().getMisspelledTokens().tokens.length > 0;
  }

  getMisspelledTokenCount(): number {
    return this.getTokens().getMisspelledTokens().tokens.length;
  }

  isCorrected(): boolean {
    return this.dto.input !== this.dto.suggestion;
  }

  withCorrectedSentence(correctedSentence: string): Sentence {
    return new Sentence({
      ...this.dto,
      suggestion: correctedSentence,
    });
  }

  withState(state: editStateEnum): Sentence {
    return new Sentence({
      ...this.dto,
      editState: state,
    });
  }
}
